// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useState, useRef } from 'react'
import type { Rect, FrameEdit, FrameChangeInfo, FrameLayer } from './types'

interface UseFrameEditStateParams {
    worldRectsById: Record<string, Rect>
    initialWorldRectsById: Record<string, Rect>
    setWorldRects: (rects: Record<string, Rect>) => void
    // We need src/metadata to populate layers initially if they are not just rects
    // For now, assuming 1:1 mapping where id is both frame and layer id
    worldRects: Array<{ id: string; src: string; mediaType?: string; rect: Rect }>
}

function rectsEqual(a: Rect, b: Rect, epsilon: number = 0.5): boolean {
    return (
        Math.abs(a.x - b.x) < epsilon &&
        Math.abs(a.y - b.y) < epsilon &&
        Math.abs(a.w - b.w) < epsilon &&
        Math.abs(a.h - b.h) < epsilon
    )
}

function getBoundingBox(layers: FrameLayer[]): Rect | null {
    if (layers.length === 0) return null
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    
    layers.forEach(layer => {
        minX = Math.min(minX, layer.currentRect.x)
        minY = Math.min(minY, layer.currentRect.y)
        maxX = Math.max(maxX, layer.currentRect.x + layer.currentRect.w)
        maxY = Math.max(maxY, layer.currentRect.y + layer.currentRect.h)
    })
    
    return {
        x: minX,
        y: minY,
        w: maxX - minX,
        h: maxY - minY
    }
}

export function useFrameEditState(params: UseFrameEditStateParams) {
    const { worldRectsById, initialWorldRectsById, setWorldRects, worldRects } = params
    const [frameEditState, setFrameEditState] = useState<Record<string, FrameEdit>>({})
    const [pendingEnableIds, setPendingEnableIds] = useState<Set<string>>(new Set())

    // Keep a ref that's always in sync with state for immediate reads
    const frameEditStateRef = useRef<Record<string, FrameEdit>>({})
    frameEditStateRef.current = frameEditState

    const enableEdit = useCallback((id: string) => {
        const worldRect = worldRectsById[id]
        if (!worldRect) {
            // Mark as pending if worldRect not available yet
            setPendingEnableIds((prev) => new Set(prev).add(id))
            return
        }

        // Find source for this id
        const item = worldRects.find(i => i.id === id)
        const src = item?.src || ''
        const mediaType = item?.mediaType || 'image'

        setFrameEditState((prev) => {
            // Don't overwrite existing edit state
            if (prev[id]) return prev

            return {
                ...prev,
                [id]: {
                    baseFrame: { ...worldRect },
                    currentFrame: { ...worldRect },
                    layers: [{
                        id: id, // Initial layer has same ID as frame (1:1 default)
                        src: src,
                        mediaType,
                        baseRect: { ...worldRect },
                        currentRect: { ...worldRect },
                        zIndex: 0
                    }],
                },
            }
        })

        // Remove from pending once initialized
        setPendingEnableIds((prev) => {
            if (!prev.has(id)) return prev
            const next = new Set(prev)
            next.delete(id)
            return next
        })
    }, [worldRectsById, worldRects])

    // Retry enabling for pending IDs when worldRects become available
    useEffect(() => {
        if (pendingEnableIds.size === 0) return

        pendingEnableIds.forEach((id) => {
            const worldRect = worldRectsById[id]
            if (worldRect && !frameEditState[id]) {
                enableEdit(id)
            }
        })
    }, [worldRectsById, pendingEnableIds, frameEditState, enableEdit])

    const disableEdit = useCallback((id: string) => {
        setFrameEditState((prev) => {
            if (!prev[id]) return prev
            const next = { ...prev }
            delete next[id]
            return next
        })

        // Also remove from pending if it was queued
        setPendingEnableIds((prev) => {
            if (!prev.has(id)) return prev
            const next = new Set(prev)
            next.delete(id)
            return next
        })
    }, [])

    const updateFrameRect = useCallback((id: string, rect: Rect) => {
        setFrameEditState((prev) => {
            const edit = prev[id]
            if (!edit) {
                console.warn('[useFrameEditState] Cannot update frame: no edit state for', id)
                return prev
            }
            return {
                ...prev,
                [id]: {
                    ...edit,
                    currentFrame: { ...rect },
                },
            }
        })
    }, [])

    const updateLayerRect = useCallback((frameId: string, layerId: string, rect: Rect) => {
        setFrameEditState((prev) => {
            const edit = prev[frameId]
            if (!edit) {
                console.warn('[useFrameEditState] Cannot update layer: no edit state for frame', frameId)
                return prev
            }
            
            const layerIndex = edit.layers.findIndex(l => l.id === layerId)
            if (layerIndex === -1) return prev

            const newLayers = [...edit.layers]
            newLayers[layerIndex] = {
                ...newLayers[layerIndex],
                currentRect: { ...rect }
            }

            return {
                ...prev,
                [frameId]: {
                    ...edit,
                    layers: newLayers,
                },
            }
        })
    }, [])

    // New: Sync Frame (Container) Rect
    // Moves the Frame to a new position (rect) and shifts ALL contents by the delta
    const syncFrameRect = useCallback((id: string, rect: Rect) => {
        setFrameEditState((prev) => {
            const edit = prev[id]
            if (!edit) return prev

            const dx = rect.x - edit.currentFrame.x
            const dy = rect.y - edit.currentFrame.y

            // Optimization: No change
            if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01 && 
                Math.abs(rect.w - edit.currentFrame.w) < 0.01 && 
                Math.abs(rect.h - edit.currentFrame.h) < 0.01) {
                return prev
            }

            // Shift all layers
            const newLayers = edit.layers.map(l => ({
                ...l,
                currentRect: {
                    ...l.currentRect,
                    x: l.currentRect.x + dx,
                    y: l.currentRect.y + dy
                }
            }))

            return {
                ...prev,
                [id]: {
                    ...edit,
                    currentFrame: { ...rect },
                    layers: newLayers,
                }
            }
        })
    }, [])

    // Sync layer with external updates (e.g. layout engine)
    // In 1:1 mode, layerId is usually frameId
    // DEPRECATED logic for new hierarchy? keeping for backwards compat or specialized moves
    const syncLayerRect = useCallback((frameId: string, layerId: string, rect: Rect) => {
        setFrameEditState((prev) => {
            const edit = prev[frameId]
            if (!edit) return prev 

            const layerIndex = edit.layers.findIndex(l => l.id === layerId)
            if (layerIndex === -1) return prev
            
            const layer = edit.layers[layerIndex]

            // Calculate position delta to move frame with image (Object Mode behavior)
            // Only applies if this is the "primary" layer (1:1 mapping)
            // For multi-layer, this logic might need refinement
            const dx = rect.x - layer.currentRect.x
            const dy = rect.y - layer.currentRect.y

            const newLayers = [...edit.layers]
            newLayers[layerIndex] = {
                ...layer,
                currentRect: { ...rect }
            }
            
            // Move frame by same delta to keep them together
            // This preserves the relative crop
            const newFrameRect = {
                ...edit.currentFrame,
                x: edit.currentFrame.x + dx,
                y: edit.currentFrame.y + dy,
            }

            return {
                ...prev,
                [frameId]: {
                    ...edit,
                    layers: newLayers,
                    currentFrame: newFrameRect,
                },
            }
        })
    }, [])

    const updateBasePositions = useCallback((id: string, rect: Rect) => {
        // Update base positions (used when real layout replaces placeholder)
        setFrameEditState((prev) => {
            const edit = prev[id]
            if (!edit) return prev

            // Update all layers base positions if they haven't been independently moved?
            // Or just update primary?
            // For 1:1, we assume the single layer updates.
            // For Multi, we should probably only update the primary layer that matches the frame ID?
            const newLayers = edit.layers.map(l => {
                if (l.id === id) {
                    return {
                        ...l,
                        baseRect: { ...rect },
                        currentRect: { ...rect }
                    }
                }
                return l
            })

            return {
                ...prev,
                [id]: {
                    ...edit,
                    baseFrame: { ...rect },
                    currentFrame: { ...rect },
                    layers: newLayers,
                },
            }
        })
    }, [])

    const fitFrameToContent = useCallback((id: string) => {
        setFrameEditState((prev) => {
            const edit = prev[id]
            if (!edit) {
                console.warn('[useFrameEditState] Cannot fit frame: no edit state for', id)
                return prev
            }
            
            const bounds = getBoundingBox(edit.layers)
            if (!bounds) return prev

            const next = {
                ...prev,
                [id]: {
                    ...edit,
                    currentFrame: { ...bounds },
                },
            }
            // Update ref immediately
            frameEditStateRef.current = next
            return next
        })
    }, [])

    const revertEdit = useCallback((id: string) => {
        const initialRect = initialWorldRectsById[id]
        if (!initialRect) {
            console.warn('[useFrameEditState] Cannot revert: no initial rect for', id)
            return
        }

        // Revert both world rects AND frame edit state to initial
        setWorldRects({
            ...worldRectsById,
            [id]: { ...initialRect },
        })

        // Find source
        const item = worldRects.find(i => i.id === id)
        const src = item?.src || ''
        const mediaType = item?.mediaType || 'image'

        setFrameEditState((prev) => {
            const edit = prev[id]
            if (!edit) return prev

            return {
                ...prev,
                [id]: {
                    baseFrame: { ...initialRect },
                    currentFrame: { ...initialRect },
                    layers: [{
                        id: id,
                        src: src,
                        mediaType,
                        baseRect: { ...initialRect },
                        currentRect: { ...initialRect },
                        zIndex: 0
                    }],
                },
            }
        })
    }, [initialWorldRectsById, worldRectsById, setWorldRects, worldRects])

    const getChangeInfo = useCallback((id: string): FrameChangeInfo => {
        const edit = frameEditState[id]
        if (!edit) {
            return {
                hasChanges: false,
                frameDiffersFromImage: false,
                frameChanged: false,
                imageChanged: false,
            }
        }

        // Check if frame matches bounding box of content
        const contentBounds = getBoundingBox(edit.layers)
        const frameDiffersFromImage = contentBounds ? !rectsEqual(edit.currentFrame, contentBounds) : false
        
        const frameChanged = !rectsEqual(edit.currentFrame, edit.baseFrame)
        
        // Check if any layer changed from base
        const imageChanged = edit.layers.some(l => !rectsEqual(l.currentRect, l.baseRect))

        // Detect layer additions/removals (initial state is always 1 layer)
        const layerCountChanged = edit.layers.length !== 1

        const hasChanges = frameChanged || imageChanged || layerCountChanged

        return {
            hasChanges,
            frameDiffersFromImage,
            frameChanged,
            imageChanged,
        }
    }, [frameEditState])

    const resetAll = useCallback(() => {
        setFrameEditState({})
        setPendingEnableIds(new Set())
    }, [])

    const commitLayoutPositions = useCallback(() => {
        setFrameEditState((prev) => {
            const next = { ...prev }
            let changed = false

            for (const id of Object.keys(prev)) {
                const worldRect = worldRectsById[id]
                if (worldRect) {
                    const edit = prev[id]
                    
                    // Update base positions to current layout
                    // Assuming 1:1 update for now as layout only tracks frame/node
                    const newLayers = edit.layers.map(l => {
                        // Only update layer if it matches the frame ID (1:1 mapping assumption for layout engine)
                        if (l.id === id) {
                            return {
                                ...l,
                                baseRect: { ...worldRect },
                                currentRect: { ...worldRect }
                            }
                        }
                        return l
                    })

                    next[id] = {
                        ...edit,
                        baseFrame: { ...worldRect },
                        currentFrame: { ...worldRect },
                        layers: newLayers,
                    }
                    changed = true
                }
            }

            return changed ? next : prev
        })
    }, [worldRectsById])

    const addLayer = useCallback((frameId: string, layer: FrameLayer) => {
         setFrameEditState((prev) => {
            const edit = prev[frameId]
            if (!edit) return prev
            
            return {
                ...prev,
                [frameId]: {
                    ...edit,
                    layers: [...edit.layers, layer].sort((a, b) => a.zIndex - b.zIndex)
                }
            }
         })
    }, [])

    const removeLayer = useCallback((frameId: string, layerId: string) => {
        setFrameEditState((prev) => {
            const edit = prev[frameId]
            if (!edit) return prev
            
            const newLayers = edit.layers.filter(l => l.id !== layerId)
            if (newLayers.length === 0) {
                console.warn('[useFrameEditState] Cannot remove last layer from frame', frameId)
                return prev
            }
            
            return {
                ...prev,
                [frameId]: {
                    ...edit,
                    layers: newLayers
                }
            }
        })
    }, [])

    const reorderLayers = useCallback((frameId: string, fromIndex: number, toIndex: number) => {
        setFrameEditState((prev) => {
            const edit = prev[frameId]
            if (!edit) return prev
            
            const sortedLayers = [...edit.layers].sort((a, b) => b.zIndex - a.zIndex)
            
            if (fromIndex < 0 || fromIndex >= sortedLayers.length || 
                toIndex < 0 || toIndex >= sortedLayers.length) {
                return prev
            }
            
            const [movedLayer] = sortedLayers.splice(fromIndex, 1)
            sortedLayers.splice(toIndex, 0, movedLayer)
            
            const newLayers = sortedLayers.map((layer, idx) => ({
                ...layer,
                zIndex: (sortedLayers.length - idx - 1) * 10
            }))
            
            return {
                ...prev,
                [frameId]: {
                    ...edit,
                    layers: newLayers
                }
            }
        })
    }, [])

    const replaceFrameEditState = useCallback((newState: Record<string, FrameEdit>) => {
        setFrameEditState(newState)
        frameEditStateRef.current = newState
    }, [])

    return {
        frameEditState,
        frameEditStateRef,
        enableEdit,
        disableEdit,
        updateFrameRect,
        updateLayerRect,
        syncLayerRect,
        syncFrameRect,
        updateBasePositions,
        fitFrameToContent,
        revertEdit,
        getChangeInfo,
        resetAll,
        commitLayoutPositions,
        addLayer,
        removeLayer,
        reorderLayers,
        replaceFrameEditState,
    }
}

export default useFrameEditState
